package pl.decks.personal.pages.blog

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaCalendar
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import pl.decks.personal.components.widgets.splitcard.SplitCard
import pl.decks.personal.components.widgets.tag.HashTag
import pl.decks.personal.components.widgets.tag.TagStylesheet.Tag
import pl.decks.personal.components.widgets.tag.TagsList
import pl.decks.personal.styles.SiteLayout

@Composable
fun BlogPostCard(
    blogPost: BlogPostEntry,
    selectedTags: List<String>,
    onBlogNavigated: () -> Unit,
) = SplitCard(BlogListingStylesheet.Card.Entry.toModifier()) {
    val ctx = rememberPageContext()
    val data = blogPost.blogPostData
    data.heroImage?.let {
        BlogPostHeroImage(it, onImageClicked = {
            ctx.router.navigateTo(blogPost.route)
            onBlogNavigated()
        })
        CardDivider()
    }
    BlogPostTitle(data.title, blogPost.route, onBlogNavigated)
    CardDivider()
    BlogPostCardSubscript(
        date = data.date,
        author = data.author,
        description = data.description,
        blogPostTags = data.tags,
        selectedTags = selectedTags,
    )
}

@Composable
private fun BlogPostHeroImage(imageSrc: String, onImageClicked: () -> Unit) = Box(
    modifier = BlogListingStylesheet.Card.ImageContainer
        .toModifier()
        .maxHeight(150.px)
        .overflow(Overflow.Hidden) // For some reason this fails to apply if done in style only
        .cursor(Cursor.Pointer)
        .onClick { onImageClicked() },
    contentAlignment = Alignment.Center,
) {
    Image(
        src = imageSrc,
        modifier = BlogListingStylesheet.Card.Image.toModifier(),
    )
}

@Composable
private fun BlogPostTitle(title: String, route: String, onBlogNavigated: () -> Unit) = Link(
    route,
    modifier = BlogListingStylesheet.Card.Title.toModifier()
        .onClick { onBlogNavigated() },
) {
    SpanText(title)
}

@Composable
private fun BlogPostCardSubscript(
    date: String?,
    author: String,
    description: String?,
    blogPostTags: List<String>,
    selectedTags: List<String>,
) = Column(BlogListingStylesheet.Card.Subscript.toModifier()) {
    BlogPostAuthorship(date, author)
    description?.let { BlogPostDescription(it) }
    BlogPostTags(blogPostTags, selectedTags)
}

@Composable
private fun BlogPostAuthorship(date: String?, author: String) = Row(
    modifier = BlogListingStylesheet.Card.Authorship.toModifier(),
    horizontalArrangement = Arrangement.SpaceBetween,
) {
    date?.let {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(SiteLayout.Space.XXSmall)
        ) {
            FaCalendar()
            SpanText(it)
        }
    }
    Spacer()
    SpanText("by: $author")
}

@Composable
private fun BlogPostDescription(description: String) = SpanText(
    description,
    modifier = BlogListingStylesheet.Card.Description.toModifier()
)

@Composable
private fun BlogPostTags(
    tags: List<String>,
    selectedTags: List<String>,
) = TagsList(Modifier.fillMaxWidth()) {
    tags.forEach { tag ->
        NavigableTag(tag, selectedTags) {
            val isSelected = selectedTags.contains(tag) || selectedTags.isEmpty()
            HashTag(tag, variant = if (isSelected) Tag.ActiveVariant else Tag.InactiveVariant)
        }
    }
}
